import type { Directive } from 'vue'
import type { AnalyticsEventName, AnalyticsParams } from './events'

type TrackBinding = { name: AnalyticsEventName; params?: AnalyticsParams }
type TrackFn = (name: AnalyticsEventName, params?: AnalyticsParams) => void
type TrackedEl = HTMLElement & { __gaTrackHandler?: EventListener }

// Vuetify 3 는 링크가 아닌(div) 클릭 가능 항목의 Enter/Space 를 DOM click 없이 컴포넌트 click 으로만 emit 한다.
// 그 경우만 keydown 으로 보완하고, a/button 은 브라우저가 click 을 만들어 주므로 제외(중복 방지).
const KEYBOARD_ITEM = 'div.v-list-item.v-list-item--link:not(.v-list-item--disabled)'

function isKeyboardActivation(el: TrackedEl, event: Event): boolean {
  if (event.type !== 'keydown') return true
  const key = (event as KeyboardEvent).key
  return event.target === el && (key === 'Enter' || key === ' ') && el.matches(KEYBOARD_ITEM)
}

function bind(el: TrackedEl, getBinding: () => TrackBinding | undefined, track: TrackFn) {
  const handler: EventListener = (event) => {
    try {
      if (!isKeyboardActivation(el, event)) return
      const b = getBinding()
      if (b && b.name) track(b.name, b.params)
    } catch (e) {
      /* noop */
    }
  }
  el.__gaTrackHandler = handler
  el.addEventListener('click', handler)
  el.addEventListener('keydown', handler)
}

function unbind(el: TrackedEl) {
  if (el.__gaTrackHandler) {
    el.removeEventListener('click', el.__gaTrackHandler)
    el.removeEventListener('keydown', el.__gaTrackHandler)
  }
  delete el.__gaTrackHandler
}

// v-track="{ name: 'ui_click', params: {...} }" — 클릭(또는 Vuetify 항목의 Enter/Space) 시 1회 발화.
// 기존 핸들러에 관여하지 않는다(버블 단계, 전파 중단 없음, preventDefault 없음).
export function createTrackDirective(track: TrackFn): Directive<TrackedEl, TrackBinding> {
  return {
    mounted(el, binding) {
      bind(el, () => binding.value, track)
    },
    updated(el, binding) {
      // 바인딩 값이 바뀌면 최신 값을 쓰도록 핸들러 교체
      unbind(el)
      bind(el, () => binding.value, track)
    },
    unmounted(el) {
      unbind(el)
    },
  }
}
